import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { weeklyTop, events } from "@/mocks/content";

function deltaBadge(delta: string) {
  if (delta.startsWith("+")) {
    return (
      <span className="flex items-center gap-0.5 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-700">
        <i className="ri-arrow-up-line"></i>
        {delta.replace("+", "")}
      </span>
    );
  }
  if (delta.startsWith("-")) {
    return (
      <span className="flex items-center gap-0.5 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800">
        <i className="ri-arrow-down-line"></i>
        {delta.replace("-", "")}
      </span>
    );
  }
  return (
    <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-medium text-foreground-600">
      持平
    </span>
  );
}

export default function Roundup() {
  const rows = useMemo(
    () => weeklyTop.map((row) => ({ ...row, event: events.find((e) => e.slug === row.slug) })),
    [],
  );
  const champion = rows[0]?.event;

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "1每日吃瓜本周瓜单",
      description: "本周讨论度最高的焦点事件排行，按热度指数排序。",
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      numberOfItems: rows.length,
      itemListElement: rows.map((row) => ({
        "@type": "ListItem",
        position: row.rank,
        name: row.event?.title,
        url: `https://77di.cn/events/${row.slug}`,
      })),
    }),
    [rows],
  );

  useSeo({
    title: "本周瓜单 | 1每日吃瓜本周热度榜单",
    description:
      "1每日吃瓜本周瓜单，按热度指数整理本周讨论度最高的焦点事件与爆款瓜，附排名变动说明与事件脉络入口，周末一次看完当周重点。",
    keywords: "1每日吃瓜本周瓜单,本周瓜单,热度榜单,大瓜汇总,焦点事件",
    path: "/roundup",
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/70 bg-background-100/60 px-4 py-10 md:px-8 md:py-14">
        <Breadcrumb items={[{ label: "本周瓜单" }]} />
        <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          1每日吃瓜本周瓜单
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          每周固定刷新一次的本周瓜单，按热度指数对当周焦点事件排序。排名变动反映的是讨论量的相对变化，仅作阅读参考。
        </p>
      </div>

      {champion && (
        <section className="w-full px-4 pt-10 md:px-8">
          <div className="relative overflow-hidden rounded-lg bg-foreground-950">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative h-56 w-full md:h-full md:min-h-[320px]">
                <img
                  src={champion.cover}
                  alt={`${champion.title} 事件配图`}
                  title={`${champion.title} - 本周瓜单榜首`}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-col justify-center p-6 md:p-10">
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-trophy-line"></i>
                  本周榜首
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-3xl">
                  {champion.title}
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-background-300">
                  {champion.summary}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-5 text-xs text-background-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-primary-400"></i>
                    热度 {champion.heat.toFixed(1)}
                  </span>
                  <span>{champion.category}</span>
                  <span>{champion.date}</span>
                </div>
                <Link
                  to={`/events/${champion.slug}`}
                  className="mt-7 flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看事件脉络
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="w-full px-4 py-12 md:px-8 md:py-16">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">完整榜单 TOP 10</h2>
        <ol className="mt-8 divide-y divide-background-200/80 overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
          {rows.map((row) => (
            <li key={row.slug}>
              <Link
                to={`/events/${row.slug}`}
                className="group flex items-center gap-4 px-4 py-5 transition-colors hover:bg-background-100/70 md:gap-6 md:px-6"
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-heading text-lg font-bold ${
                    row.rank <= 3
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200 text-foreground-700"
                  }`}
                >
                  {row.rank}
                </span>
                <span className="hidden h-16 w-24 shrink-0 overflow-hidden rounded-md md:block">
                  <img
                    src={row.event?.cover}
                    alt={`${row.event?.title} 事件配图`}
                    title={`${row.event?.title} - 1每日吃瓜本周瓜单`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] text-secondary-900">
                      {row.event?.category}
                    </span>
                    {deltaBadge(row.delta)}
                  </span>
                  <span className="line-clamp-2 mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                    {row.event?.title}
                  </span>
                  <span className="mt-1 hidden text-sm text-foreground-500 sm:line-clamp-1">
                    {row.event?.summary}
                  </span>
                </span>
                <span className="hidden shrink-0 items-center gap-1 text-sm font-medium text-foreground-700 sm:flex">
                  <i className="ri-fire-line text-primary-500"></i>
                  {row.heat.toFixed(1)}
                </span>
              </Link>
            </li>
          ))}
        </ol>

        <div className="mt-10 rounded-lg border border-background-200/80 bg-background-100/60 p-6 md:p-8">
          <h3 className="font-heading text-lg font-bold text-foreground-950">
            关于热度指数的说明
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            热度指数综合了站内阅读量、讨论增量与话题扩散速度等因素，用一个相对直观的数值呈现在同一时间窗口内的关注程度。它的作用是帮助读者快速排序，而不是对事件本身做出评价，也不代表任何形式的定论。
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}